import { useQueries } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { avatarInitials, avatarSlotFor } from '../../../lib/avatar-color';
import { longRelativeTime, shortRelativeTime } from '../../../lib/contact-rows';
import { useTranslation } from '../../../lib/i18n';
import { profileLinksApi } from '../../../lib/profile-links-api';
import { useProfileLinksEnabled } from '../../../lib/profile-links-flag';
import { leadOpenTarget, peopleLeadLinks, peopleLeadRows, type PeopleLeadRow } from '../../../lib/profile-links-model';
import { queryKeys } from '../../../lib/query-client';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useNarrow } from '../../../lib/use-narrow';
import { useProfileLinksList } from '../../../lib/use-profile-links';
import { Avatar, Card, Typography } from '../ui';
import { CategoryList, CategoryListGroupHeader, useCategoryRowClass } from '../category-view/CategoryView';
import styles from '../contacts/contacts.module.css';

/**
 * "From your profile links" — the lead cards in People (design §8.3, the P4–P7 shared contract), above the
 * Outreach Contacts list. iOS and Android draw the same section under the same key.
 *
 * A row: the visitor's name (self-asserted), their phone (always shown, Q5), the link they came through
 * (its context title, or "Main profile") and when they first wrote. It opens the owner-side conversation
 * when the lead has one, else that link's People tab.
 *
 * Behind `specs.SymphonyProfileLinks` (`useProfileLinksEnabled`, fail-closed): off, nothing is read and
 * nothing renders. It renders nothing either while loading, on a failure, or with no leads — it is an
 * addition to People, never a state of it, so the contacts below never wait on it or wear its error.
 */
export function ProfileLinkLeadsSection() {
  const { t } = useTranslation();
  const enabled = useProfileLinksEnabled();
  const list = useProfileLinksList(enabled);
  const links = useMemo(() => peopleLeadLinks(list.data?.links ?? []), [list.data]);
  const leads = useQueries({
    queries: links.map((link) => ({
      queryKey: queryKeys.profileLinkLeads(link.id),
      queryFn: () => profileLinksApi.leads(link.id),
      enabled,
      retry: false,
    })),
  });
  const leadsKey = leads.map((q) => q.dataUpdatedAt).join(',');
  const rows = useMemo(
    () => peopleLeadRows(links, Object.fromEntries(links.map((link, i) => [link.id, leads[i]?.data?.leads]))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [links, leadsKey],
  );

  if (!enabled || rows.length === 0) return null;
  return (
    <CategoryList>
      <CategoryListGroupHeader>{t('profileLinks.leads.section')}</CategoryListGroupHeader>
      {rows.map((row) => (
        <ProfileLeadRow key={row.key} row={row} />
      ))}
    </CategoryList>
  );
}

function ProfileLeadRow({ row }: { row: PeopleLeadRow }) {
  const { t } = useTranslation();
  const router = useRouter();
  const basePath = useBasePath();
  const narrow = useNarrow();
  const rowSurface = useCategoryRowClass();
  const { logPageClickBi } = useGenericBi();
  const { lead } = row;
  const name = lead.name.trim() || t('contacts.row.unnamed');
  const meta = [lead.phone, row.linkPath, row.linkLabel ?? t('profileLinks.main')].filter(Boolean).join(' · ');

  const open = () => {
    const target = leadOpenTarget(row);
    logPageClickBi(
      { page_name: 'contacts_page', button_name: 'open_profile_lead' },
      { additionalFields: { target: target.kind } },
    );
    if (target.kind === 'conversation') {
      const params = new URLSearchParams({ conversation: target.conversationId, chat: 'fullscreen' });
      window.location.assign(`${basePath}/?${params.toString()}`);
      return;
    }
    const route = `/profile-links/${encodeURIComponent(target.linkId)}?tab=people`;
    void router.push(route, `${basePath}${route}`);
  };

  return (
    <Card
      variant="sunken"
      size={narrow ? 'sm' : 'lg'}
      disablePadding={!narrow}
      component="button"
      interactive
      className={`${narrow ? styles.rowCompact : styles.row}${rowSurface ? ` ${rowSurface}` : ''}`}
      onClick={open}
      data-testid="profile-lead-row"
    >
      <Avatar initials={avatarInitials(name)} size={narrow ? 40 : 32} color={avatarSlotFor(lead.id)} />
      <span className={narrow ? styles.rowRightCenter : styles.rowMain}>
        <span className={styles.rowMain}>
          <Typography variant="h4" color="primary" className={styles.rowName} dir="auto">
            {name}
          </Typography>
          <Typography variant="p3" color="secondary" className={styles.rowSubtitle} dir="auto">
            {narrow ? meta : [meta, longRelativeTime(lead.at)].filter(Boolean).join(' · ')}
          </Typography>
        </span>
        {narrow ? (
          <span className={styles.rowEnd}>
            <Typography variant="p3" color="secondary" className={styles.rowAge}>
              {shortRelativeTime(lead.at, undefined, t('contacts.time.now'))}
            </Typography>
            <ChevronRight size={24} className="dir-flip" aria-hidden />
          </span>
        ) : null}
      </span>
    </Card>
  );
}
